import { Box, SxProps, Theme, Typography } from "@mui/material";
import TextFieldComponent, {
  TextFieldComponentProps,
  TextFieldRef,
} from "components/input/TextFieldComponent";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import LabelHeader, { LabelHeaderProps } from "./LabelHeader";

interface SpecsProps {
  labelProps: LabelHeaderProps;
  sxBox?: SxProps<Theme>;
  sxBoxValue?: SxProps<Theme>;
  sxError?: SxProps<Theme>;
  sxTypography?: SxProps<Theme>;
  value?: string | number | null;
  isTextField?: boolean;
  onHasError?: (error: boolean) => void;
  validate?: () => boolean;
  textFieldProps?: TextFieldComponentProps;
}

export interface SpecRef {
  validate: () => boolean;
}

const defaultStyleBoxValue = {
  display: "flex",
  width: "60%",
  backgroundColor: "var(--offwhite-400)",
  border: "3px solid var(--silver-150)",
  alignItems: "center",
  px: 2,
  height: "48px",
};

/**
 * A functional component that renders specs for a label.
 * @param {object} props - The properties object.
 */
const Specs = forwardRef<SpecRef, SpecsProps>(
  (
    {
      sxBox,
      sxBoxValue,
      sxError,
      sxTypography,
      labelProps,
      isTextField,
      onHasError,
      textFieldProps,
      value,
    }: SpecsProps,
    ref,
  ) => {
    const { sxBox: sxBoxLabel, ...restLabelProps } = labelProps;
    const { sx: sxTextField, value: valueTextField, ...restTextFieldProps } = textFieldProps || {};
    const [errorMessages, setErrorMessages] = useState<string>("");
    const textFieldRef = useRef<TextFieldRef>(null);
    useEffect(() => {
      if (onHasError && errorMessages) {
        onHasError(true);
      } else if (onHasError) {
        onHasError(false);
      }
    }, [errorMessages]);

    useImperativeHandle(ref, () => ({
      validate() {
        if (isTextField && textFieldRef.current) {
          return textFieldRef.current.validate();
        }
        return !errorMessages;
      },
    }));

    return (
      <Box sx={{ width: "100%" }}>
        <Box sx={{ display: "flex", ...sxBox }}>
          <LabelHeader
            {...restLabelProps}
            sxBox={{ width: "40%", boxSizing: "border-box", ...sxBoxLabel }}
          />
          {isTextField ? (
            <TextFieldComponent
              ref={textFieldRef}
              sx={{
                ...defaultStyleBoxValue,
                borderRadius: 0,
                px: 0,
                backgroundColor: "var(--white)",
                ...sxTextField,
              }}
              sxRoot={{ borderRadius: 0, width: "100%" }}
              sxInput={{ borderRadius: 0, height: "42px", px: 2, py: 0 }}
              sxNotchedOutline={{ border: "none" }}
              value={valueTextField || value}
              {...restTextFieldProps}
              isHiddenError
              onError={(error, message) => {
                setErrorMessages(message);
              }}
            />
          ) : (
            <Box sx={{ ...defaultStyleBoxValue, ...sxBoxValue, boxSizing: "border-box" }}>
              <Typography sx={{ ...sxTypography }}>{value}</Typography>
            </Box>
          )}
        </Box>
        {errorMessages && (
          <Box sx={{ ml: 2, mt: "2px", mb: "6px", color: "var(--red-800)", ...sxError }}>
            {errorMessages}
          </Box>
        )}
      </Box>
    );
  },
);

export default Specs;
